/**
 * Constraint solver for the group editor's link system.
 *
 * One pure function: given the current children, current links, and the
 * dragged child's new pose, return either the updated children array (with
 * any RP offset auto-updates the caller should also persist), or a
 * `{ reject, conflicts }` result that the caller maps to "snap back."
 *
 * Design doc: `claude-docs/2026-05-09-group-link-constraint-solver.md`;
 * the link graph it now serves: `reference-docs/layout-relationships/overview.md`.
 *
 * Lives in shared-lib (CommonJS) so the server enforces links too. Physics
 * is INJECTED: the editor passes `opts.physicalAxes(child, allChildren)` for
 * its stack/wall snaps and `opts.wallPlaneFor(wallRef, child)` for `on-wall`
 * links; the server passes what it can compute. Nothing here reads
 * room state or a React-side helper.
 *
 * Pins and fixed nodes
 *
 *   The solve is seeded from PINS — the dragged child (legacy single-pin
 *   call) or any number of `{ id → pose }` entries — plus every FIXED NODE a
 *   link names (`opts.fixedNodes`: refKey → anchor-local pose for openings /
 *   walls). A fixed node is a pin the user did not place: it never moves,
 *   and a drag that implies a different pose for it is a conflict.
 *
 * Model
 *
 *   The dragged child is the only user-pinned child for a tick. From it,
 *   we BFS the constraint graph:
 *
 *     - SYMMETRIC links (centerLine, rail-y, rail-x, copy/mirror) carry
 *       movement to their other endpoints. The other endpoint joins the
 *       moving set with an implied pose.
 *     - RP links fire downhill: when source is in the moving set and
 *       target isn't, target joins at `source.pose ⊕ link.offset`.
 *     - When an RP target is itself in the moving set (it moved as a
 *       consequence of the drag, via a symmetric link or another RP) and
 *       the source is NOT, the RP is *suppressed*: the source stays put
 *       and the link's stored `offset` is recomputed to match the new
 *       positions on commit.
 *
 *   Wall-mounted children are pinned to their wall plane via a hard
 *   projection at the end of the BFS. A wall-mounted child in the moving
 *   set whose pose leaves the wall is a conflict — except for the
 *   directly-dragged child, which we project onto the wall (the user's
 *   intent overrides).
 *
 *   If any constraint implies a pose for a child already in the moving
 *   set that disagrees with the existing pose (beyond an epsilon), it's
 *   a conflict and we reject the drag.
 *
 * Copy/Mirror expansion
 *
 *   Copy/mirror links are between two same-type "parents" and implicitly
 *   couple their RP-children index-by-index (see the original
 *   `propagateLinks` Pass 3 docstring for the index-pair rule). Rather
 *   than encode this as a special case in the BFS, we expand each
 *   copy/mirror link into N synthetic `linked-pair` links — one per
 *   matching RP-child pair — at solve start. A `linked-pair` is a
 *   binary symmetric link that says
 *     `childB - parentB == sign * (childA - parentA)`
 *   and the BFS handles it like any other symmetric link.
 */

const { pairCopyMirrorChildren } = require('./snapshot');
const { canonicalType, RAIL_EPS } = require('./linkSemantics');
const { refKey } = require('./refs');

// Pose match epsilon. Tighten if false negatives appear (real conflicts
// missed); loosen if false positives appear (phantom conflicts from
// floating-point noise during drag).
const TRANSLATION_EPS = 5e-4; // meters (0.5 mm)
const ROTATION_EPS = 1e-3; // radians (~0.057°)

// Rails compare at the measured alignment noise floor, not the rp epsilon;
// see linkSemantics.RAIL_EPS.
const translationEpsFor = (link) => {
  const t = canonicalType(link?.type);
  // A copy/mirror pair is real geometry on both sides (two sconces hung by
  // hand), so its synthetic linked-pair shares the rails' noise floor.
  return t === 'center-on' || t === 'rail-y' || t === 'linked-pair'
    ? RAIL_EPS
    : TRANSLATION_EPS;
};

// A center-on's members in BFS-id space: object ids as-is, fixed geometry
// members as their refKey (never collides with an object id).
function centerOnMemberIds(link) {
  return [
    ...(link.memberIds || []),
    ...(link.fixedMembers || []).map((r) => refKey(r)),
  ];
}

// Canonical names — compare through `canonicalType(link.type)` so stored
// `centerLine` / `rail-x` behave as `frame` / `center-on`.
const SYMMETRIC_TYPES = new Set([
  'frame',
  'rail-y',
  'center-on',
  'linked-pair', // synthesized from copy/mirror
]);

function near(a, b, eps) {
  return Math.abs((a || 0) - (b || 0)) <= eps;
}

function poseFromChild(c) {
  return {
    dx: c.dx || 0,
    dy: c.dy || 0,
    dz: c.dz || 0,
    rotYRel: c.rotYRel || 0,
  };
}

// ── Per-link-type implications ─────────────────────────────────────────

/**
 * For symmetric links, given one endpoint's resolved pose, return the
 * implied pose for each other endpoint. Returns an array of
 * `{ otherId, impliedPose }` entries.
 *
 * `parentPoses` maps parentId → pose, used by `linked-pair` to look up
 * its parents' current poses (from `movingSet` if they're moving, else
 * from the baseline `state` map).
 */
function implySymmetric(link, currentId, currentPose, parentPoses, fixedNodes) {
  const linkType = canonicalType(link.type);
  if (linkType === 'frame') {
    const otherId =
      link.objectAId === currentId ? link.objectBId : link.objectAId;
    // A fixed axis (a doorway, a wall midpoint) overrides the free lineX.
    const axisPose = link.axis ? fixedNodes.get(refKey(link.axis)) : null;
    const lineX = axisPose ? axisPose.dx || 0 : link.lineX || 0;
    const rotMode = link.rotationMode || 'copy';
    let rotYRel;
    if (rotMode === 'copy') rotYRel = currentPose.rotYRel;
    else if (rotMode === 'mirror') rotYRel = -(currentPose.rotYRel || 0);
    else rotYRel = null; // 'none' — leave partner's rotation alone
    return [
      {
        otherId,
        impliedPose: {
          dx: 2 * lineX - currentPose.dx,
          dy: currentPose.dy,
          dz: currentPose.dz,
          rotYRel,
        },
      },
    ];
  }

  if (linkType === 'rail-y' || linkType === 'center-on') {
    const axis = linkType === 'rail-y' ? 'dy' : 'dx';
    const memberIds =
      linkType === 'center-on' ? centerOnMemberIds(link) : link.memberIds || [];
    const out = [];
    for (const mid of memberIds) {
      if (mid === currentId) continue;
      // The rail only constrains its axis; other components are unset
      // so the BFS preserves the partner's baseline values. The pose
      // shape is normalized in `addOrCheck` below.
      out.push({
        otherId: mid,
        impliedPose: { [axis]: currentPose[axis] },
      });
    }
    return out;
  }

  if (link.type === 'linked-pair') {
    // childB - parentB == sign * (childA - parentA)
    // Both children + both parents are in the link descriptor.
    const isA = link.childAId === currentId;
    const otherChildId = isA ? link.childBId : link.childAId;
    const myParentId = isA ? link.parentAId : link.parentBId;
    const otherParentId = isA ? link.parentBId : link.parentAId;
    const sign = link.sign || 1;
    const myParent = parentPoses(myParentId);
    const otherParent = parentPoses(otherParentId);
    if (!myParent || !otherParent) return [];
    return [
      {
        otherId: otherChildId,
        impliedPose: {
          dx:
            (otherParent.dx || 0) +
            sign * ((currentPose.dx || 0) - (myParent.dx || 0)),
          dy:
            (otherParent.dy || 0) +
            ((currentPose.dy || 0) - (myParent.dy || 0)),
          dz:
            (otherParent.dz || 0) +
            ((currentPose.dz || 0) - (myParent.dz || 0)),
          rotYRel:
            (otherParent.rotYRel || 0) +
            sign * ((currentPose.rotYRel || 0) - (myParent.rotYRel || 0)),
        },
      },
    ];
  }

  return [];
}

/**
 * RP target pose given the source's current pose.
 *
 * `offset` is stored in source-local frame for `rotationMode: 'copy'`
 * and in anchor frame for `rotationMode: 'none'`. `rotYDiff` only
 * propagates under 'copy'.
 */
function implyRpTarget(link, sourcePose) {
  const mode = link.rotationMode || 'copy';
  const offset = link.offset || { dx: 0, dy: 0, dz: 0 };
  const theta = mode === 'copy' ? sourcePose.rotYRel || 0 : 0;
  const c = Math.cos(theta);
  const s = Math.sin(theta);
  const ax = (offset.dx || 0) * c + (offset.dz || 0) * s;
  const ay = offset.dy || 0;
  const az = -(offset.dx || 0) * s + (offset.dz || 0) * c;
  return {
    dx: (sourcePose.dx || 0) + ax,
    dy: (sourcePose.dy || 0) + ay,
    dz: (sourcePose.dz || 0) + az,
    rotYRel:
      mode === 'copy' ? (sourcePose.rotYRel || 0) + (link.rotYDiff || 0) : null, // 'none' — target's rotation is independent
  };
}

/**
 * Recompute an RP link's stored `offset` (and `rotYDiff` under 'copy')
 * from the current resolved poses of source and target. Used at commit
 * time for suppressed RPs.
 */
function recomputeRpOffset(link, sourcePose, targetPose) {
  const mode = link.rotationMode || 'copy';
  const dxA = (targetPose.dx || 0) - (sourcePose.dx || 0);
  const dyA = (targetPose.dy || 0) - (sourcePose.dy || 0);
  const dzA = (targetPose.dz || 0) - (sourcePose.dz || 0);
  const theta = mode === 'copy' ? sourcePose.rotYRel || 0 : 0;
  const c = Math.cos(theta);
  const s = Math.sin(theta);
  const offset = {
    dx: dxA * c - dzA * s,
    dy: dyA,
    dz: dxA * s + dzA * c,
  };
  const update = { offset };
  if (mode === 'copy') {
    update.rotYDiff = (targetPose.rotYRel || 0) - (sourcePose.rotYRel || 0);
  }
  return update;
}

// ── Copy/Mirror expansion ──────────────────────────────────────────────

/**
 * Expand each copy/mirror link into one `linked-pair` per matching
 * RP-child pair (by child type, paired index-by-index in workingLinks
 * creation order — the same pairing rule as the original
 * `propagateLinks` Pass 3).
 *
 * The synthesized links are passed to the BFS alongside the original
 * links (minus the copy/mirror originals, which the BFS doesn't know
 * how to handle directly).
 */
function expandCopyMirror(links, childTypeById) {
  const out = [];
  for (const link of links) {
    if (link.type !== 'copy' && link.type !== 'mirror') {
      out.push(link);
      continue;
    }
    for (const pair of pairCopyMirrorChildren(link, links, childTypeById)) {
      out.push({
        // Synthetic id so callers can ignore these in UI/persistence.
        id: `__synth_lp_${link.id}_${pair.type}_${pair.index}`,
        type: 'linked-pair',
        parentAId: link.parentAId,
        parentBId: link.parentBId,
        childAId: pair.aRp.targetId,
        childBId: pair.bRp.targetId,
        sign: pair.sign,
        // Tag for debugging / conflict reporting.
        synthFromLinkId: link.id,
      });
    }
  }
  return out;
}

// ── Solver ────────────────────────────────────────────────────────────

/**
 * @param {Object[]} children      — current working children array
 * @param {Object[]} links         — current working links
 * @param {string|Map|Array} pins  — dragged id (+ dragPose next), or Map<id,pose> / [{id,pose}]
 * @param {Object}   [dragPose]    — { dx, dy, dz, rotYRel } when `pins` is an id
 * @param {Object}   [opts]        — { physicalAxes?, wallPlaneFor?, fixedNodes?, projectAll? }
 *
 * @returns {Object} either:
 *   { ok: true, children: Object[], suppressedRpUpdates: [{ linkId, offset, rotYDiff? }] }
 *   { reject: true, conflicts: [{ linkId, childId, reason }] }
 */
function solveConstraints(
  children,
  links,
  pinsOrDraggedId,
  dragPoseOrOpts,
  maybeOpts
) {
  // Legacy single-pin call: (children, links, draggedId, dragPose, opts).
  // Multi-pin call: (children, links, Map<id,pose> | [{id,pose}], opts).
  let pins;
  let opts;
  if (typeof pinsOrDraggedId === 'string') {
    pins = new Map([[pinsOrDraggedId, dragPoseOrOpts || {}]]);
    opts = maybeOpts || {};
  } else if (pinsOrDraggedId instanceof Map) {
    pins = pinsOrDraggedId;
    opts = dragPoseOrOpts || {};
  } else if (Array.isArray(pinsOrDraggedId)) {
    pins = new Map(pinsOrDraggedId.map((p) => [p.id, p.pose || {}]));
    opts = dragPoseOrOpts || {};
  } else {
    pins = new Map();
    opts = dragPoseOrOpts || {};
  }
  const physical = opts.physicalAxes || (() => ({}));
  const wallPlaneFor = opts.wallPlaneFor || null;
  const fixedNodes =
    opts.fixedNodes instanceof Map ? opts.fixedNodes : new Map();
  // Enforcement mode (resolveGroup): project decided axes onto EVERY child,
  // not only the moving set, so an on-wall subject that drifted off its wall
  // is put back even when nothing it is linked to moved. The editor leaves
  // this off — an unrelated drag must not snap a bystander.
  const projectAll = !!opts.projectAll;

  // Pins seed the solve; so do FIXED NODES (an opening a member is centred
  // on) — a group whose only member is bound to geometry has nothing to pin
  // and still has somewhere to go.
  if (!Array.isArray(children) || (pins.size === 0 && fixedNodes.size === 0)) {
    return {
      ok: true,
      children: children || [],
      suppressedRpUpdates: [],
      unsatisfied: [],
    };
  }

  const childById = new Map(children.map((c) => [c.id, c]));
  const childTypeById = new Map(children.map((c) => [c.id, c.type || null]));

  // Expand copy/mirror into linked-pair before BFS.
  const expandedLinks = expandCopyMirror(links || [], childTypeById);

  // Index links by endpoint for cheap lookup during BFS.
  const linksByChildId = new Map();
  const addLinkToIndex = (id, link) => {
    if (!linksByChildId.has(id)) linksByChildId.set(id, []);
    linksByChildId.get(id).push(link);
  };
  for (const link of expandedLinks) {
    if (link.type === 'rp') {
      addLinkToIndex(link.sourceId, link);
      addLinkToIndex(link.targetId, link);
    } else if (canonicalType(link.type) === 'frame') {
      addLinkToIndex(link.objectAId, link);
      addLinkToIndex(link.objectBId, link);
    } else if (
      canonicalType(link.type) === 'rail-y' ||
      canonicalType(link.type) === 'center-on'
    ) {
      const ids =
        canonicalType(link.type) === 'center-on'
          ? centerOnMemberIds(link)
          : link.memberIds || [];
      for (const mid of ids) addLinkToIndex(mid, link);
    } else if (canonicalType(link.type) === 'on-wall') {
      addLinkToIndex(link.subjectId, link);
    } else if (link.type === 'linked-pair') {
      addLinkToIndex(link.childAId, link);
      addLinkToIndex(link.childBId, link);
    }
    // copy/mirror originals dropped during expansion — they don't appear
    // in expandedLinks; their effect is via the synthetic linked-pair.
  }

  // Seed: every pin at its requested pose (unset components filled from
  // its current child entry), then every fixed node any link names, at its
  // known pose. Fixed nodes go in AFTER pins so a pin on a fixed node's id
  // (never expected) still loses to geometry.
  const movingSet = new Map();
  const queue = [];
  for (const [id, pose] of pins) {
    const child = childById.get(id);
    if (!child) continue;
    movingSet.set(id, { ...poseFromChild(child), ...(pose || {}) });
    queue.push(id);
  }
  for (const [key, pose] of fixedNodes) {
    if (!linksByChildId.has(key)) continue;
    movingSet.set(key, { dx: 0, dy: 0, dz: 0, rotYRel: 0, ...pose });
    queue.push(key);
  }
  if (movingSet.size === 0) {
    return { ok: true, children, suppressedRpUpdates: [], unsatisfied: [] };
  }
  const suppressedRps = [];
  const conflicts = [];
  // The link that put each member into the moving set. A member placed by a
  // rail and re-implied by an rp (or the reverse) is checked at the LOOSER
  // of the two epsilons: the rail's noise floor is the whole point of it.
  const placedBy = new Map();

  // Lookup parent pose for linked-pair: prefer movingSet, fall back to
  // baseline. Returns null if parent doesn't exist.
  const parentPoses = (id) => {
    if (movingSet.has(id)) return movingSet.get(id);
    const c = childById.get(id);
    return c ? poseFromChild(c) : null;
  };

  // Strip the target's physically-fixed axes from `impliedPose` so links
  // never compete with physics. A wall-mounted target's `dz`/`rotYRel`
  // are decided by the wall plane, not by RP/centerLine/etc; a counter-
  // mounted sink's `dy` is decided by the countertop. The projection
  // pass below sets the actual values.
  // An `on-wall` link pins the subject's dz / rotYRel to its wall plane —
  // the stated form of the wall projection `physicalAxes` used to infer
  // from the object's type. Both are merged into one "physically decided"
  // axis set.
  const onWallAxes = (childId, childTemplate) => {
    if (!wallPlaneFor) return {};
    const link = (linksByChildId.get(childId) || []).find(
      (l) => canonicalType(l.type) === 'on-wall' && l.subjectId === childId
    );
    if (!link) return {};
    const plane = wallPlaneFor(link.wall, childTemplate);
    if (!plane) return {};
    const out = {};
    if (plane.dz != null) out.dz = plane.dz;
    if (plane.rotYRel != null) out.rotYRel = plane.rotYRel;
    return out;
  };
  const decidedAxes = (childId, childTemplate, list) => ({
    ...physical(childTemplate, list),
    ...onWallAxes(childId, childTemplate),
  });

  const stripFixedAxes = (impliedPose, otherId) => {
    const childTemplate = childById.get(otherId);
    if (!childTemplate) return impliedPose;
    const fixed = decidedAxes(otherId, childTemplate, children);
    const fixedKeys = Object.keys(fixed);
    if (fixedKeys.length === 0) return impliedPose;
    const stripped = { ...impliedPose };
    for (const axis of fixedKeys) stripped[axis] = null;
    return stripped;
  };

  // Add `otherId` to movingSet at `impliedPose`, or if already present,
  // verify the implied pose matches the existing one. Some implied
  // poses are partial (rail-y constrains only `dy`; fixed-axis stripping
  // sets some components to null); fill in the unconstrained components
  // from the partner's baseline so the stored pose is complete.
  const addOrCheck = (otherId, impliedPoseRaw, sourceLink) => {
    const impliedPose = stripFixedAxes(impliedPoseRaw, otherId);
    const baseline = poseFromChild(childById.get(otherId) || {});
    const fullPose = {
      dx: impliedPose.dx != null ? impliedPose.dx : baseline.dx,
      dy: impliedPose.dy != null ? impliedPose.dy : baseline.dy,
      dz: impliedPose.dz != null ? impliedPose.dz : baseline.dz,
      rotYRel:
        impliedPose.rotYRel != null ? impliedPose.rotYRel : baseline.rotYRel,
    };
    if (movingSet.has(otherId)) {
      const existing = movingSet.get(otherId);
      // Only check the components this implication actually constrains.
      // null components (either because the link is partial — e.g. rail-y
      // — or because the target's fixed axes were stripped) skip the
      // mismatch check on that axis.
      const tEps = Math.max(
        translationEpsFor(sourceLink),
        translationEpsFor(placedBy.get(otherId))
      );
      const partialMatch =
        (impliedPose.dx == null || near(existing.dx, fullPose.dx, tEps)) &&
        (impliedPose.dy == null || near(existing.dy, fullPose.dy, tEps)) &&
        (impliedPose.dz == null || near(existing.dz, fullPose.dz, tEps)) &&
        (impliedPose.rotYRel == null ||
          near(existing.rotYRel, fullPose.rotYRel, ROTATION_EPS));
      if (!partialMatch) {
        conflicts.push({
          // A synthetic linked-pair reports the copy/mirror it came from —
          // that is the link a caller can relax or show.
          linkId: sourceLink.synthFromLinkId || sourceLink.id,
          childId: otherId,
          reason: 'pose-mismatch',
        });
      }
      return;
    }
    movingSet.set(otherId, fullPose);
    placedBy.set(otherId, sourceLink);
    queue.push(otherId);
  };

  while (queue.length) {
    const current = queue.shift();
    const currentPose = movingSet.get(current);
    const touching = linksByChildId.get(current) || [];
    for (const link of touching) {
      if (link.type === 'rp') {
        if (link.sourceId === current) {
          // Source-side: pull target.
          const impliedPose = implyRpTarget(link, currentPose);
          addOrCheck(link.targetId, impliedPose, link);
        } else {
          // Target-side: suppression unless source is also in moving set.
          if (!movingSet.has(link.sourceId)) {
            suppressedRps.push(link);
          }
          // If source IS in moving set, the source-side branch ran (or
          // will run when it's processed) and called addOrCheck for
          // this target — no extra work here.
        }
      } else if (SYMMETRIC_TYPES.has(canonicalType(link.type))) {
        const implications = implySymmetric(
          link,
          current,
          currentPose,
          parentPoses,
          fixedNodes
        );
        for (const { otherId, impliedPose } of implications) {
          addOrCheck(otherId, impliedPose, link);
        }
      }
    }
  }

  // Physical projection: every moving-set member with decided axes
  // (wall-mounted dz/rotYRel, counter-mounted dy) gets those components
  // overwritten by the physically-determined value. Always wins — links
  // can't fight physics, so this is unconditional, not a conflict source.
  // Read from MS-merged children so a stack-snap target tracks any moving
  // determinant (countertop in MS uses MS pose; today this is moot since
  // countertops aren't draggable, but it's robust for future shapes).
  const msMergedChildren = children.map((c) => {
    if (!movingSet.has(c.id)) return c;
    const p = movingSet.get(c.id);
    return { ...c, dx: p.dx, dy: p.dy, dz: p.dz, rotYRel: p.rotYRel };
  });
  for (const [id, pose] of movingSet) {
    const childTemplate = childById.get(id);
    if (!childTemplate) continue;
    const fixed = decidedAxes(id, childTemplate, msMergedChildren);
    const fixedEntries = Object.entries(fixed);
    if (fixedEntries.length === 0) continue;
    let next = pose;
    for (const [axis, value] of fixedEntries) {
      const eps = axis === 'rotYRel' ? ROTATION_EPS : TRANSLATION_EPS;
      if (!near(next[axis] || 0, value, eps)) {
        next = { ...next, [axis]: value };
      }
    }
    if (next !== pose) movingSet.set(id, next);
  }

  if (projectAll) {
    for (const c of children) {
      if (movingSet.has(c.id)) continue;
      const fixed = decidedAxes(c.id, c, msMergedChildren);
      const entries = Object.entries(fixed);
      if (entries.length === 0) continue;
      const base = poseFromChild(c);
      let next = base;
      for (const [axis, value] of entries) {
        const eps = axis === 'rotYRel' ? ROTATION_EPS : TRANSLATION_EPS;
        if (!near(next[axis] || 0, value, eps))
          next = { ...next, [axis]: value };
      }
      if (next !== base) movingSet.set(c.id, next);
    }
  }

  if (conflicts.length > 0) {
    return { reject: true, conflicts };
  }

  // Commit: build the new children array from movingSet + baseline.
  const newChildren = children.map((c) => {
    if (!movingSet.has(c.id)) return c;
    const p = movingSet.get(c.id);
    return {
      ...c,
      dx: p.dx,
      dy: p.dy,
      dz: p.dz,
      rotYRel: p.rotYRel,
    };
  });

  // Compute auto-updates for suppressed RP offsets, using the resolved
  // poses of source and target (source is NOT in movingSet by the
  // suppression rule, so use baseline; target IS in movingSet).
  const suppressedRpUpdates = [];
  for (const link of suppressedRps) {
    const sourcePose = movingSet.has(link.sourceId)
      ? movingSet.get(link.sourceId)
      : poseFromChild(childById.get(link.sourceId) || {});
    const targetPose = movingSet.get(link.targetId);
    if (!targetPose) continue;
    const update = recomputeRpOffset(link, sourcePose, targetPose);
    suppressedRpUpdates.push({
      linkId: link.id,
      offset: update.offset,
      rotYDiff: update.rotYDiff,
    });
  }

  // `unsatisfied` is reserved for step 2's ordered relaxation (a link kept
  // but not met); today a solve either meets every link or rejects.
  return {
    ok: true,
    children: newChildren,
    suppressedRpUpdates,
    unsatisfied: [],
  };
}

module.exports = { solveConstraints, TRANSLATION_EPS, ROTATION_EPS };
